import type { HttpApiServerType } from '@/services/http-api/httpApi';
import { CurlVariant } from '@/services/http-api/transformHttpApi';
import * as sdk from '@tarslib/sdk';
import type { ICodeSample } from '@tarslib/types/lib/httpApi';
import type { IPostRequestContextVariablesItem } from '@tarslib/types/lib/postRequest';
import type { Any } from '../constant/any';

const escapeStringWin = (str: string) => {
  try {
    return str
      .replace(/\\/g, '\\\\')
      .replace(/%(?=[a-zA-Z0-9_])/g, '%^')
      .replace(/[\r\n]/g, '');
  } catch {
    return str;
  }
};

const fixLanguageByVariant = (
  newCodeSamples: ICodeSample[],
  syntaxMode: string,
  variant: string,
  language: string,
) => {
  let innerLanguage = language;
  let innerVariant = variant;
  const codeSample = newCodeSamples.find(({ id }) => String(id) === innerVariant);

  switch (syntaxMode) {
    case 'powershell': {
      switch (variant) {
        case CurlVariant.Linux:
          innerLanguage = 'curl';
          break;
        case CurlVariant.Window:
          innerLanguage = 'curl';
          break;
        case 'PowerShell':
          innerLanguage = 'powershell';
          innerVariant = 'RestMethod';
          break;
        default:
          break;
      }
      break;
    }
    case 'javascript': {
      if (
        typeof variant === 'string' &&
        ['Axios', 'Native', 'Request', 'Unirest'].includes(variant)
      ) {
        innerLanguage = 'nodejs';
      }
      break;
    }
    default:
      break;
  }
  return {
    language: innerLanguage,
    variant: innerVariant,
    code: codeSample?.code,
    prompt: codeSample?.prompt,
  };
};

const resolveRequest = (request: typeof Any, variant: string) => {
  const currentRequest = new sdk.HttpRequest(request);
  // @ts-ignore
  if (currentRequest.headers?.members?.length) {
    // @ts-ignore
    const members = currentRequest.headers.members;
    // 将数字变为字符串，防止生成代码参数缺失
    // @ts-ignore
    members.forEach((_, index) => {
      const value = members[index].value;
      members[index].value = typeof value === 'number' ? `${value}` : value;
    });
  }
  if (variant !== CurlVariant.Window) return currentRequest;

  // 去掉body中的回车
  if (currentRequest.body?.raw) {
    currentRequest.body.raw = escapeStringWin(currentRequest.body.raw);
  }
  return currentRequest;
};

const generateRequest = async (
  request: typeof Any,
  language: string,
  variant: string,
  variables: IPostRequestContextVariablesItem[] = [],
): Promise<string> => {
  const options: Record<string, string | number | boolean> = {
    indentCount: 3,
    indentType: 'Space',
    trimRequestBody: true,
    followRedirect: true,
  };
  let currentOptions = { ...options };
  let currentVariant = variant;
  if (variant === CurlVariant.Linux || variant === CurlVariant.Window) {
    currentVariant = CurlVariant.Linux;
  }
  if (variant === CurlVariant.Window) {
    currentOptions = { ...options, lineContinuationCharacter: '^', quoteType: 'double' };
  }

  try {
    // @ts-ignore
    const postmanCodeGenerators = await import('postman-code-generators');

    let result = await new Promise<string>((resolve) => {
      postmanCodeGenerators.default.convert(
        language,
        currentVariant,
        resolveRequest(request, variant),
        currentOptions,
        (error: Error | null, res: string | undefined) => {
          if (error) {
            resolve('');
          } else {
            resolve(res || '');
          }
        },
      );
    });
    variables.forEach(({ name, value }) => {
      const matchStr = `{{${name}}}`;
      if (typeof result === 'string') {
        result = result.replaceAll(matchStr, value);
      }
    });
    return (result as string | undefined) || '';
  } catch (e) {
    if (e) {
      //
    }
    return '';
  }
};

export async function generateRequestSampleCode(
  httpApi: Pick<HttpApiServerType, 'codeSamples' | 'codeSampleLanguages'>,
  request: typeof Any,
  variables: IPostRequestContextVariablesItem[],
  languageVariant:
    | {
        language: string;
        variant: string;
      }
    | undefined,
  example: string | undefined,
) {
  const languages = httpApi.codeSampleLanguages;
  const codeSamples = httpApi.codeSamples;

  const selectedLanguageString = languageVariant?.language ?? languages[0]?.key;
  const selectedLanguage =
    languages.find((item) => item.key === selectedLanguageString) ?? languages[0];
  if (!selectedLanguage) {
    return undefined;
  }
  const selectedVariant = languageVariant?.variant ?? languages[0]?.variants[0]?.key;
  const variantToSet = selectedLanguage.variants.find((item) => item.key === selectedVariant);
  const variant = variantToSet?.key || selectedLanguage.variants[0]?.key;
  if (!variant) {
    return undefined;
  }

  const {
    language: fixLanguage,
    variant: fixVariant,
    code,
    prompt,
  } = fixLanguageByVariant(codeSamples, selectedLanguage.syntaxMode, variant, selectedLanguage.key);
  const currentCodeSnippet =
    code ??
    (await generateRequest(
      (() => {
        if (example && request?.body?.mode === 'raw') {
          return {
            ...request,
            body: {
              ...request.body,
              raw: example,
            },
          };
        }
        return request;
      })(),
      fixLanguage,
      String(fixVariant),
      variables,
    ));
  if (!currentCodeSnippet) {
    return undefined;
  }

  return {
    code: currentCodeSnippet,
    language: selectedLanguage.key,
    variant: variant,
    codeBlockLanguage: selectedLanguage.syntaxMode,
    prompt,
  };
}
